/**
 * The one formatter for a stamped `kpi` figure (plots plan F), on every layout
 * (tile, row, grid) and every surface.
 *
 * Python rounds once, half away from zero, and stamps the decimals the figure prints
 * with (`PlotFigure.decimals`). This prints exactly those decimals and never
 * rounds again: `13%` and `13.4%` are two figures Python stamped with 0 and 1
 * decimals, not one figure this function decided about. Numbers are grouped
 * and punctuated by `Intl` in the request locale; a currency prints its symbol
 * only when Python stamped an ISO code (a null code is never guessed).
 *
 * Stored payloads that carry no `decimals` never reach this function: their
 * read adapter keeps today's legacy formatter for that shape.
 */

import { PlotFigure } from '@digital-twins/slide-schemas/generated/plot-types'

import { roundHalfAwayFromZeroAt } from '../../foundations/producer-rounding'

/** What an unlabelled deck prints in; every current payload renders as `en`. */
export const KPI_FIGURE_FALLBACK_LOCALE = 'en'

export interface KpiFigureFormatOptions {
  locale?: string | null
  /** The `plot_unit_pp` template (`{v} pp`), when the payload carries one. */
  unitPpTemplate?: string | null
}

function safeLocale(locale: string | null | undefined): string {
  return typeof locale === 'string' && locale.trim() ? locale.trim() : KPI_FIGURE_FALLBACK_LOCALE
}

/**
 * Prints `value` at `decimals`. The half-away-from-zero step is Python's own rule
 * (`roundHalfAwayFromZeroAt`), applied before `Intl` so a value stamped unrounded
 * (a deck tile keeps its raw value; only its decimals are stamped) never
 * meets `Intl`'s half-expand rounding: 62.25 at one decimal prints 62.2 here
 * and in Python. A value Python already rounded passes through unchanged.
 */
/** The typographic minus every surface prints a negative figure with ("−4 pp", "−23"). */
const MINUS_SIGN = '\u2212'

function fixedNumber(value: number, decimals: number, locale: string, signed = false): string {
  return intlNumber(value, decimals, locale, signed).replace(/^-/, MINUS_SIGN)
}

function intlNumber(value: number, decimals: number, locale: string, signed: boolean): string {
  const rounded = roundHalfAwayFromZeroAt(value, decimals)
  const options: Intl.NumberFormatOptions = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay: signed ? 'exceptZero' : 'auto',
  }
  try {
    return new Intl.NumberFormat(locale, options).format(rounded)
  } catch {
    return new Intl.NumberFormat(KPI_FIGURE_FALLBACK_LOCALE, options).format(rounded)
  }
}

function scaleCeiling(scaleMax: number | null | undefined, locale: string): string | null {
  if (typeof scaleMax !== 'number' || !Number.isFinite(scaleMax)) {
    return null
  }
  const decimals = Number.isInteger(scaleMax) ? 0 : 1
  return fixedNumber(scaleMax, decimals, locale)
}

function currency(value: number, figure: PlotFigure, locale: string): string {
  const code = figure.currencyCode
  if (!code) {
    return fixedNumber(value, figure.decimals, locale)
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: figure.decimals,
      maximumFractionDigits: figure.decimals,
      // A currency prints through `Intl` on the value as stamped, as the deck
      // tile always has: its declared places, never a second rounding rule.
    }).format(value)
  } catch {
    return `${code} ${fixedNumber(value, figure.decimals, locale)}`
  }
}

/**
 * An NPS score (-100 to 100) at `decimals`, always signed (owner decision
 * 2026-10-01): "+25", "−25" with the typographic minus, and "0" bare. The one
 * rule every surface that prints an NPS level uses; a change in the score
 * keeps its own signed formatter and is never signed twice.
 */
export function formatNpsScore(value: number, decimals: number, locale?: string | null): string {
  return fixedNumber(value, decimals, safeLocale(locale), true)
}

/**
 * The printed figure, or `null` when Python stamped no value (the caller draws
 * its "not available" chrome; a missing value is never drawn as 0).
 */
export function formatKpiFigure(
  figure: PlotFigure,
  options: KpiFigureFormatOptions = {},
): string | null {
  const { value } = figure
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null
  }
  const locale = safeLocale(options.locale)
  switch (figure.format) {
    case 'percent':
      return `${fixedNumber(value, figure.decimals, locale)}%`
    case 'mean': {
      const printed = fixedNumber(value, figure.decimals, locale)
      const ceiling = scaleCeiling(figure.scaleMax, locale)
      return ceiling ? `${printed}/${ceiling}` : printed
    }
    case 'points': {
      const printed = fixedNumber(value, figure.decimals, locale, true)
      const template = options.unitPpTemplate
      return template && template.includes('{v}') ? template.replace('{v}', printed) : `${printed} pp`
    }
    case 'currency':
      return currency(value, figure, locale)
    case 'nps':
      return formatNpsScore(value, figure.decimals, locale)
    case 'count':
    case 'index':
    default:
      return fixedNumber(value, figure.decimals, locale)
  }
}
